import { zodResolver } from '@hookform/resolvers/zod';
import type { ActiveShift, DesktopShift, DesktopUpdates } from '@repo/desktop-bridge';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import { PIN_MAX_DIGITS, pinSchema, staffCodeSchema } from '@/lib/field-schemas';

import { AuthFrame, fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { paths } from '../paths';
import { PinKeypad } from './PinKeypad';
import { messageForShiftRefusal, TILL_UNAVAILABLE } from './till-messages';
import { TillActions, tillScroll } from './TillFrame';

/**
 * Who is at the till, established against the roster the device cached.
 *
 * **There is no server route for this, and that is the point.** The PIN goes
 * down to the main process and an outcome comes back; it is checked against a
 * verifier the renderer never sees, and the check works with the line down —
 * which is the whole offline promise. Everything sold afterwards is queued
 * carrying this person's staff code, and the server re-checks who they are when
 * it arrives.
 *
 * So starting a shift here is not a claim that the sales will be accepted. It is
 * a claim that this device, with what it last heard, has no reason to refuse the
 * person in front of it.
 *
 * `react-hook-form` + `zod` behind the registry's `Form`, like every other form
 * in this app — there is no second way to build one — and the two field rules
 * are imported from `lib/field-schemas.ts` rather than restated, because a PIN
 * shape written twice is a PIN shape that eventually disagrees with itself.
 *
 * ## Why it is a duty board — issue #213
 *
 * **The screen was a 384px form centred in a 1280px field**: measured at
 * 1280x800, 896px of horizontal nothing and 296px of vertical nothing around it,
 * with the sentence that carries the whole weight of the moment — *every ticket
 * you sell is recorded against you* — set as 14px of grey text under the title.
 *
 * The emptiness was the symptom. **The missing keypad was the fault**: the PIN
 * is `^\d{4,6}$` by contract and this is a touch terminal whose own conventions
 * are *"thumb first, minimal typing"*, so the one value a cashier types at the
 * start of every shift, standing, had no way to be tapped. A device that fills
 * the field also fills the screen, which is why one change answers both.
 *
 * A government gate has a register you sign on to, and that is the shape the
 * language already specifies: a **margin** carrying what you are taking on, a
 * **field** carrying the act. `AuthFrame` composes the signed-out screens the
 * same way, and this deliberately borrows the *idea* and not the rail — that
 * rail is back-office signed-out language and this is a terminal, built to the
 * till column of "The two scales".
 *
 * ## The staff code cannot share the keypad
 *
 * Codes at this park are names — `ASHA01`, `PRIYA`, `RAJ`, `MEERA`, `DEE` — so
 * they are alphanumeric and keep a text field. **Two input modes on one screen
 * is the constraint this layout is designed around** rather than one it hides:
 * the keypad stands beside the pair of fields, under the label of the one it
 * fills, and the other field is plainly a different kind of thing.
 */

const REQUIRED_PIN = 'Enter your PIN.';

const startShiftSchema = z.object({
  staffCode: staffCodeSchema,
  // `pinSchema` and not `optionalPinSchema`: everywhere else a blank PIN means
  // "leave the one they have", and here it means nothing at all.
  pin: z.string().min(1, REQUIRED_PIN).pipe(pinSchema),
});

type StartShiftValues = z.infer<typeof startShiftSchema>;

export function StartShift({
  shift,
  terminal,
  switcher,
  printer,
  updates,
  version,
  onStarted,
}: {
  shift: DesktopShift;
  /**
   * This device's ticket prefix, stated in the margin as the thing being taken
   * on. Optional because the frame can be drawn before the device has answered.
   */
  terminal?: string;
  /**
   * The other outlets this machine sells for (#243), in a row under *Start
   * shift* (#312). Nobody is signed in here, so nobody is carried across: the
   * next person signs in against whichever outlet is chosen. Mid-shift the
   * switch is beside the outlet plate instead, and keeps the cashier signed in
   * (#254).
   */
  switcher?: ReactNode;
  /**
   * The till's printer setting (#261), beside the screen's name. About the
   * device rather than a shift, so nobody has to sign on to reach it.
   */
  printer?: ReactNode;
  /**
   * A new version downloaded and waiting (#288). Said here and nowhere else on
   * the till: this is the screen between shifts, and nobody is mid-sale.
   */
  updates?: DesktopUpdates | null;
  /**
   * The build the running shell reports (#306), in the same fact as the update
   * line: what the till is on now, and — when one is waiting — what it moves to.
   * `null` or absent draws no version, and the update line stands alone as before.
   */
  version?: string | null;
  onStarted: (started: ActiveShift) => void;
}) {
  const form = useForm({
    resolver: zodResolver(startShiftSchema),
    mode: 'onTouched',
    defaultValues: { staffCode: '', pin: '' },
  });

  const [formError, setFormError] = useState<string | null>(null);
  const [updateWaiting, setUpdateWaiting] = useState<string | null>(null);

  useEffect(() => {
    let current = true;

    // A failed read is no line, never a message: an update is not the
    // counter's problem.
    updates
      ?.waiting()
      .then((next) => {
        if (current) setUpdateWaiting(next);
      })
      .catch(() => undefined);

    return () => {
      current = false;
    };
  }, [updates]);

  const pending = form.formState.isSubmitting;

  /**
   * The PIN, read from the form and written back to it — **one value, and the
   * keypad borrows it rather than keeping one.**
   *
   * `watch` plus `setValue` and not a `useState` beside the resolver: a second
   * copy fails on the case a terminal with a keyboard plugged into it produces
   * every day, where two digits are typed and two are tapped. There is nothing
   * to reconcile here because there is nothing to reconcile *with*.
   *
   * **`shouldValidate` only once the field has already reported.** Validating on
   * every tap would put *"A PIN is 4 to 6 digits"* under the field while
   * somebody is entering the third of them — the "telling you off for being
   * unfinished" failure `docs/ui-conventions.md` records. Once a message is up,
   * re-checking is the right thing: it clears as soon as they have fixed it.
   */
  const pin = form.watch('pin');
  const pinHasReported = form.formState.errors.pin !== undefined;

  const handleSubmit = async ({ staffCode, pin }: StartShiftValues) => {
    if (pending) return;

    setFormError(null);

    try {
      const outcome = await shift.start({ staffCode, pin });

      if (!outcome.started) {
        setFormError(messageForShiftRefusal(outcome.reason));

        // The PIN and only the PIN. Retyping a staff code to correct a mistyped
        // digit is a cruelty at a counter with a queue at it, and the staff code
        // is not the secret.
        form.resetField('pin');

        return;
      }

      onStarted(outcome.shift);
    } catch {
      // Silent. A PIN passed through this function and it is not written
      // anywhere, including to a console.
      setFormError(TILL_UNAVAILABLE);
      form.resetField('pin');
    }
  };

  return (
    <>
      {/* The screen's own name, in the frame's upper zone beside the mark — the
          same slot the counter screens put their shift-level chrome in, and the
          same lettering the footer states *TILL* in. It is a label and not a
          heading: the heading is in the margin below, and it is the act rather
          than the screen. */}
      <TillActions label="Start a shift">{printer}</TillActions>

      {/* **`my-auto` and not `justify-center`.** They look identical until the
          content outgrows the window, and then centred flex content overflows
          past the top edge with nothing to scroll back to — on a screen whose
          submit button is the last thing in it.

          **`md:` and not `lg:`, and that is the whole of what issue #209 found
          the hard way.** Tailwind's `lg` is 1024px and **the terminals run at
          1000x625**, so a two-column composition gated on `lg` would stack on
          the only hardware that matters and be wide only in a screenshot. This
          one engages at 768px, which is below every window the till is ever in.

          `max-w-[55rem]` is the composition's own width — 12rem of margin, the
          two gaps, 24rem of fields and 15rem of keypad — rather than a round
          number, and it is centred so the slack at 1280 is split rather than
          left in a slab down one side.

          **And it sits in a scroll region, because `my-auto` only promises the
          overflow goes downward — something still has to scroll it (#308).**
          `TillFrame` clips at `main`, so with several outlets in the switch list
          the margin ran past the footer datum and *Add an outlet* was
          unreachable. The region is `flex-1` and the grid keeps `my-auto` in it:
          centred while it fits, scrolled from the top once it does not. */}
      <div className={`flex min-h-0 flex-1 flex-col ${tillScroll}`}>
        <div className="mx-auto my-auto grid w-full max-w-[55rem] gap-x-8 gap-y-10 md:grid-cols-[12rem_minmax(0,1fr)]">
          {/* The margin: what you are taking on. It carries no border — the datum
            above and below it is the page's rule, and a second one here would be
            the boldness spent twice. */}
          <div className="md:col-start-1">
            <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              Sign on.
            </h1>

            {/* **Promoted out of 14px of grey chrome.** This sentence is the
              reason the screen exists — it is the accountability a cashier
              accepts by signing on — and it was previously the tail of a
              sentence about which fields to fill in. */}
            <p className="mt-4 text-sm text-foreground">
              Every ticket you sell is recorded against you.
            </p>

            <div className="mt-8 space-y-4 border-t border-border pt-6">
              {terminal !== undefined && (
                <AuthFrame.Fact label="This terminal">{terminal}</AuthFrame.Fact>
              )}

              {/* Stated here rather than discovered at the end of the shift. The
                count is this person's own — `EndShift` says the same thing from
                the other side — and knowing it at sign-on is what makes the
                drawer theirs for the next eight hours. */}
              <AuthFrame.Fact label="The drawer">
                Counted at the end of your shift, by you.
              </AuthFrame.Fact>

              {/* One fact for the two things a till says about itself (#306): the
                build it is on, and the one it will move to. The version leads
                and the update sentence is kept word for word after it, so
                "1.0.1 now, 1.0.2 next." and *A new version is ready* are read as
                one thought under one label rather than as two notices. */}
              {(version != null || updateWaiting !== null) && (
                <AuthFrame.Fact label="This till">
                  {version != null && (
                    <span className="block">
                      {updateWaiting === null ? version : `${version} now, ${updateWaiting} next.`}
                    </span>
                  )}
                  {updateWaiting !== null && (
                    <span className="block">
                      A new version is ready. It installs when the till is closed.
                    </span>
                  )}
                </AuthFrame.Fact>
              )}
            </div>

            {/* **The door to the back office, for registering this device to
              another outlet — issue #259.** A registered till opens here and
              nowhere else, so without it a second outlet could only be added
              through DevTools. In the margin with the outlets rather than in the
              field, so it never sits beside *Start shift*, and outline because
              the accent belongs to that button.

              `/` is `TerminalOutOfService`'s door: the guard turns it into the
              back-office sign-in for anybody signed out, and a PIN taken here
              grants nothing there. The way back is `BackToTill`, which signs
              the back office out as it goes.

              **Beside a switch list the note goes.** Measured at 1000x625 with
              two outlets, when the list was in this margin: the note ran 45px
              past the footer datum. The list has moved under *Start shift*
              (#312) and the door stayed: it is not a switch, and the row it
              would end is labelled *Switch to*. The note still goes, because the
              first time a till is given a second outlet it has no list yet, and
              that is when the note is read.

              **And while an update is waiting, the note goes too (#306).** At
              1000x625 the update line alone ran this column 19px past the footer
              datum, and the version beside it made that 39px — the note was
              painting over *Till*. The door stays; only its explanation waits
              for a shift without an update. */}
            <div className="mt-8">
              <Button
                variant="outline"
                size="touch"
                className="w-full justify-start"
                nativeButton={false}
                render={<Link to={paths.dashboard} />}
              >
                Add an outlet
              </Button>
              {switcher === undefined && updateWaiting === null && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Registers this till for another outlet. An owner signs in to the back office to do
                  it.
                </p>
              )}
            </div>
          </div>

          {/* The field: the act, and under it the other outlets (#312). The
            switch is a sibling of the form and not inside it, so none of its
            buttons can ever be taken for the form's submit. */}
          <div className="md:col-start-2">
            <Form {...form}>
              <form noValidate onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
                {/* **The fields and the keypad are siblings, not a stack, and that
                was measured.** Stacked — the arrangement the ticket drew — the
                register is 560px tall empty and 648px with a refusal showing,
                against 459px of field at 1000x625: the submit button leaves the
                window, which is the exact failure #209 found on `Take payment`.
                Side by side the tallest column is the keypad's 260px and the
                whole register is 356px with the refusal in it.

                **Both columns have a floor, and that is the other #209 lesson.**
                Free space is shared by flex-basis, so a `flex-1` with base 0
                beside a content-sized sibling resolves to 0px; here both are
                `flex-1` with a `min-w` under them, so the narrow end of the
                range shrinks them toward those floors instead of collapsing one.
                `items-start` so the keypad does not stretch to whatever height a
                refusal has just given the fields. */}
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  <div className="w-full min-w-60 flex-1 space-y-5 sm:max-w-sm">
                    <FormField
                      control={form.control}
                      name="staffCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required className={fieldLabel}>
                            Staff code
                          </FormLabel>
                          <FormControl
                            render={
                              <Input
                                // `h-touch` and not `h-11`: this is the till scale,
                                // and the floor for anything tapped is
                                // `--size-touch`.
                                className={`${fieldSurface} h-touch font-mono text-lg numeric`}
                                required
                                // **No placeholder, against the general rule in
                                // `docs/ui-conventions.md`.** That rule wants the
                                // shape of a good answer shown, and it is right on a
                                // form that creates a record. Here the shape of a
                                // good answer is somebody's actual staff code:
                                // `MEERA` sitting in a field on a shared terminal
                                // reads as a form already filled in, which is the
                                // one thing a sign-on screen must not look like. The
                                // label says what the field is, and nothing about it
                                // is ambiguous without an example.
                                autoComplete="off"
                                autoCapitalize="characters"
                                autoFocus
                                {...field}
                                // **Uppercased in the field, not in the submit
                                // handler.** `staff_code` is stored uppercase and
                                // compared case-insensitively — the migration says
                                // so in as many words — so lowercasing is a
                                // difference the person can see and the server
                                // cannot. Doing it here means what they read is what
                                // is sent. `autoCapitalize` above is a hint to an
                                // on-screen keyboard and transforms nothing.
                                onChange={(event) => {
                                  field.onChange(event.currentTarget.value.toUpperCase());
                                }}
                              />
                            }
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required className={fieldLabel}>
                            PIN
                          </FormLabel>
                          <FormControl
                            render={
                              <Input
                                type="password"
                                className={`${fieldSurface} h-touch font-mono text-lg tracking-wider numeric`}
                                required
                                // No placeholder, and here it is not a judgement
                                // call: a masked field showing `4827` in the clear
                                // is a PIN-shaped string on a terminal the public
                                // stands in front of.
                                //
                                // `inputMode` gives a numeric keypad without making
                                // this a number field — that would strip a leading
                                // zero from a PIN that has one. `maxLength` is the
                                // same bound the keypad stops at, taken from the
                                // same constant, so the two sources refuse the
                                // seventh digit together.
                                inputMode="numeric"
                                maxLength={PIN_MAX_DIGITS}
                                autoComplete="off"
                                {...field}
                              />
                            }
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Above the submit and it stays there. A toast vanishes, and
                    somebody who looked away to serve a visitor has no way to get
                    it back. */}
                    {formError !== null && (
                      <Alert variant="destructive">
                        <IconAlertTriangle />
                        <AlertDescription>{formError}</AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Beside the fields rather than under them, and the two floors
                  are what keep it there. Below `sm` it follows them down the
                  page; it is never hidden at any width, because a keypad
                  somebody can lose is worse than no keypad. */}
                  <PinKeypad
                    className="w-full min-w-48 flex-1 sm:max-w-60"
                    value={pin}
                    max={PIN_MAX_DIGITS}
                    disabled={pending}
                    onChange={(next) => {
                      form.setValue('pin', next, {
                        shouldDirty: true,
                        shouldValidate: pinHasReported,
                      });
                    }}
                  />
                </div>

                <Button type="submit" size="touch" className="mt-8 w-full" disabled={pending}>
                  {pending ? 'Checking…' : 'Start shift'}
                </Button>
              </form>
            </Form>

            {/* **Under the button and not in the margin — issue #312.** Stacked
            in the margin, four outlets made that column scroll at the Acer's
            880x515 while this one had 120px empty under *Start shift*. A row
            that wraps spends that width instead; the scroll region above is
            still the fallback when a refusal adds height. */}
            {switcher !== undefined && <div className="mt-4">{switcher}</div>}
          </div>
        </div>
      </div>
    </>
  );
}
